import cls from 'classnames';
import { useState, useMemo, memo, useCallback, useEffect, useRef } from 'react';
import { NavLink, generatePath } from 'react-router-dom';
import { Image, Button } from 'react-bootstrap';
import { TooltipControl } from 'src/components/base/Tooltip';
import { MarkerSymbols } from 'src/components/base/MapView';
import { EventPriority } from 'src/components/Trap/Elements';
import { useModalDialog, ConfirmModalDialog } from 'src/components/base/ModalDialog';

import classnames from './BuildingMapsPanel.module.scss';

export const BuildingMapsPanel = ({ items, editMode, selectedItem, onSelectItem, onCenterChanged, map, isValidBuildingPosition, onRemoveFromMap, onItemPositionChange }) => {
  const mapContainerRef = useRef();
  const imageRef = useRef();
  const metricsCacheRef = useRef(null);
  const [selected, setSelected] = useState();
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [loadedImageKey, setLoadedImageKey] = useState(null);
  const [dragUpdateKey, setDragUpdateKey] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const currentMapId = map?.id ?? null;
  const mapFileUrl = map?.file_url ?? null;
  const currentImageKey = map ? `${map.id ?? 'no-id'}::${mapFileUrl ?? ''}` : null;
  const isCurrentImageLoaded = imageLoaded && loadedImageKey === currentImageKey;

  const validItems = useMemo(() => items.filter(item => isValidBuildingPosition(item.changed, currentMapId)), [items, currentMapId, isValidBuildingPosition]);

  // Handle window resize to recalculate positions
  const [resizeKey, setResizeKey] = useState(0);

  useEffect(() => {
    if (!mapFileUrl || !currentImageKey) {
      metricsCacheRef.current = null;
      setLoadedImageKey(null);
      setImageLoaded(false);
      return;
    }

    if (loadedImageKey === currentImageKey) {
      return;
    }

    if (typeof window === 'undefined' || typeof window.Image !== 'function') {
      return;
    }

    const preload = new window.Image();

    const handleLoad = () => {
      setLoadedImageKey(currentImageKey);
      setImageLoaded(true);
    };

    const handleError = () => {
      if (loadedImageKey !== currentImageKey) {
        setImageLoaded(false);
        setLoadedImageKey(null);
        metricsCacheRef.current = null;
      }
    };

    preload.addEventListener('load', handleLoad, { once: true });
    preload.addEventListener('error', handleError, { once: true });
    preload.src = mapFileUrl;

    if (preload.complete && preload.naturalWidth > 0) {
      handleLoad();
    }

    return () => {
      preload.removeEventListener('load', handleLoad);
      preload.removeEventListener('error', handleError);
    };
  }, [currentImageKey, loadedImageKey, mapFileUrl]);

  const getImageMetrics = useCallback(() => {
    if (!mapContainerRef.current || !imageRef.current) {
      return metricsCacheRef.current;
    }

    const containerRect = mapContainerRef.current.getBoundingClientRect();
    const naturalWidth = imageRef.current.naturalWidth;
    const naturalHeight = imageRef.current.naturalHeight;

    if (!naturalWidth || !naturalHeight || !containerRect.width || !containerRect.height) {
      return metricsCacheRef.current;
    }

    const scale = Math.min(
      containerRect.width / naturalWidth,
      containerRect.height / naturalHeight
    );

    const displayWidth = naturalWidth * scale;
    const displayHeight = naturalHeight * scale;
    const offsetX = (containerRect.width - displayWidth) / 2;
    const offsetY = (containerRect.height - displayHeight) / 2;

    const metrics = { displayWidth, displayHeight, offsetX, offsetY };
    metricsCacheRef.current = metrics;
    return metrics;
  }, [resizeKey]);

  // Convert relative coordinates (0-1) to pixel coordinates within the container
  const relativeToPixel = useCallback((relativePos) => {
    const metrics = getImageMetrics();

    if (!metrics) {
      return { x: 0, y: 0 };
    }

    const { displayWidth, displayHeight, offsetX, offsetY } = metrics;

    return {
      x: offsetX + relativePos.x * displayWidth,
      y: offsetY + relativePos.y * displayHeight
    };
  }, [getImageMetrics]);

  // Convert pixel coordinates (relative to container) to relative coordinates (0-1)
  const pixelToRelative = useCallback((pixelPos) => {
    const metrics = getImageMetrics();

    if (!metrics) {
      return { x: 0, y: 0 };
    }

    const { displayWidth, displayHeight, offsetX, offsetY } = metrics;
    const normalizedX = (pixelPos.x - offsetX) / displayWidth;
    const normalizedY = (pixelPos.y - offsetY) / displayHeight;

    return {
      x: Math.max(0, Math.min(1, normalizedX)),
      y: Math.max(0, Math.min(1, normalizedY))
    };
  }, [getImageMetrics]);

  useEffect(() => {
    if (selectedItem && isValidBuildingPosition(selectedItem.changed, currentMapId)) {
      setSelected([selectedItem]);
    } else {
      setSelected(null);
    }
  }, [selectedItem, currentMapId, isValidBuildingPosition]);

  // Recalculate marker positions when items change (e.g., after save)
  useEffect(() => {
    if (isCurrentImageLoaded) {
      setResizeKey(prev => prev + 1);
    }
  }, [items, isCurrentImageLoaded]);

  useEffect(() => {
    let resizeTimeout;

    const handleResize = () => {
      // Debounce resize events to prevent excessive re-renders
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (isCurrentImageLoaded) {
          // Force re-render to recalculate pixel positions for all markers
          setResizeKey(prev => prev + 1);
          // Also update selected items to recalculate info window position
          setSelected(selected => selected ? [...selected] : null);
        }
      }, 100); // 100ms debounce
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [isCurrentImageLoaded]);

  // Handle image element resize using ResizeObserver
  useEffect(() => {
    if (!imageRef.current || !isCurrentImageLoaded) return;

    const resizeObserver = new ResizeObserver(() => {
      // Image dimensions changed, recalculate all positions
      setResizeKey(prev => prev + 1);
      setSelected(selected => selected ? [...selected] : null);
    });

    resizeObserver.observe(imageRef.current);

    return () => resizeObserver.disconnect();
  }, [isCurrentImageLoaded]);

  const requestFullscreen = useCallback(() => {
    const element = mapContainerRef.current;
    if (!element) { return; }

    const request = element.requestFullscreen
      || element.webkitRequestFullscreen
      || element.msRequestFullscreen
      || element.mozRequestFullScreen;

    if (request) {
      try {
        const result = request.call(element);
        if (result && typeof result.catch === 'function') {
          result.catch(() => {});
        }
      } catch (error) {
        // Ignore request failures (e.g. user gesture requirements or unsupported browsers)
      }
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    const exit = document.exitFullscreen
      || document.webkitExitFullscreen
      || document.msExitFullscreen
      || document.mozCancelFullScreen;

    if (exit) {
      try {
        const result = exit.call(document);
        if (result && typeof result.catch === 'function') {
          result.catch(() => {});
        }
      } catch (error) {
        // Ignore errors triggered when not in full screen state
      }
    }
  }, []);

  const handleFullscreenChange = useCallback(() => {
    const fullscreenElement = document.fullscreenElement
      || document.webkitFullscreenElement
      || document.msFullscreenElement
      || document.mozFullScreenElement;

    setIsFullScreen(fullscreenElement === mapContainerRef.current);
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [handleFullscreenChange]);

  useEffect(() => {
    return () => {
      const fullscreenElement = document.fullscreenElement
        || document.webkitFullscreenElement
        || document.msFullscreenElement
        || document.mozFullScreenElement;

      if (fullscreenElement === mapContainerRef.current) {
        exitFullscreen();
      }
    };
  }, [exitFullscreen]);

  const handleToggleFullscreen = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isFullScreen) {
      exitFullscreen();
    } else {
      requestFullscreen();
    }
  }, [exitFullscreen, isFullScreen, requestFullscreen]);

  const handleMapClick = useCallback((e) => {
    if (!mapContainerRef.current || !isCurrentImageLoaded) return;

    // Check if click is on the container itself (map background), not on a child element
    // Since image has pointerEvents: 'none', clicks on image bubble to container
    const isClickOnMapBackground = e.target === mapContainerRef.current;

    if (!isClickOnMapBackground) {
      // Click was on a child element (marker or info window), don't handle here
      return;
    }

    // If in edit mode, place trap at clicked position
    if (editMode && onCenterChanged) {
      const containerRect = mapContainerRef.current.getBoundingClientRect();

      // Calculate position relative to container
      const pixelX = e.clientX - containerRect.left;
      const pixelY = e.clientY - containerRect.top;

      const relativePos = pixelToRelative({ x: pixelX, y: pixelY });

      onCenterChanged(relativePos);
    } else if (selected) {
      // If not in edit mode and info is open, close it when clicking on map background
      handleCloseInfo();
    }
  }, [editMode, onCenterChanged, isCurrentImageLoaded, pixelToRelative, selected, handleCloseInfo]);

  const handleMarkerMouseDown = useCallback((e, item) => {
    if (!editMode || !isCurrentImageLoaded || !mapContainerRef.current) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const containerRect = mapContainerRef.current.getBoundingClientRect();
    const pixelPos = relativeToPixel(item.changed);
    const pointerX = e.clientX - containerRect.left;
    const pointerY = e.clientY - containerRect.top;
    const offsetX = pointerX - pixelPos.x;
    const offsetY = pointerY - pixelPos.y;
    
    setDragging(item);
    setDragOffset({ x: offsetX, y: offsetY });
    setSelected([item]);
    onSelectItem(item);
  }, [editMode, onSelectItem, isCurrentImageLoaded, relativeToPixel]);

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !isCurrentImageLoaded || !mapContainerRef.current) return;

    const containerRect = mapContainerRef.current.getBoundingClientRect();
    const pointerX = e.clientX - containerRect.left;
    const pointerY = e.clientY - containerRect.top;
    const pixelX = pointerX - dragOffset.x;
    const pixelY = pointerY - dragOffset.y;

    // Convert pixel position to relative coordinates
    const relativePos = pixelToRelative({ x: pixelX, y: pixelY });
    
    // Update the dragging item's changed position to make marker follow cursor
    dragging.changed = { ...relativePos, map_id: currentMapId };

    // Force re-render to update marker position during drag
    setDragUpdateKey(prev => prev + 1);
  }, [dragging, dragOffset, isCurrentImageLoaded, pixelToRelative, currentMapId]);

  const handleMouseUp = useCallback(() => {
    if (dragging) {
      // Show trap info on release
      setSelected([dragging]);
      onSelectItem(dragging);
      if (typeof onItemPositionChange === 'function') {
        onItemPositionChange(dragging);
      }
    }
    
    setDragging(null);
    setDragOffset({ x: 0, y: 0 });
  }, [dragging, onSelectItem, onItemPositionChange]);

  const handleMarkerClick = useCallback((item) => {
    setSelected([item]);
    onSelectItem(item);
  }, [onSelectItem]);

  const handleCloseInfo = useCallback(() => {
    setSelected(null);
    onSelectItem && onSelectItem(null);
  }, [onSelectItem]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && selectedItem) {
        onSelectItem && onSelectItem(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedItem, onSelectItem]);

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  if (!map?.file_url) {
    return (
      <div className="w-100 h-100 d-flex align-items-center justify-content-center">
        <div className="text-muted text-center">
          <i className="bi bi-image" style={{ fontSize: '3rem' }}></i>
          <div className="mt-2">{__('No building map available')}</div>
        </div>
      </div>
    );
  }

  const hasImageMetrics = !!getImageMetrics();

  return (
    <div className="w-100 h-100 position-relative overflow-hidden">
      <div 
        ref={mapContainerRef}
        className="w-100 h-100 position-relative"
        onClick={handleMapClick}
        style={{ cursor: editMode ? 'crosshair' : 'default' }}
      >
        <div className="position-absolute top-0 end-0 p-2" style={{ zIndex: 1002 }}>
          <button
            type="button"
            className="btn btn-light border shadow-sm d-flex align-items-center justify-content-center"
            style={{ width: 36, height: 36 }}
            onClick={handleToggleFullscreen}
            onMouseDown={(event) => event.stopPropagation()}
            title={isFullScreen ? __('Exit full screen') : __('View full screen')}
            aria-label={isFullScreen ? __('Exit full screen') : __('View full screen')}
          >
            <i className={`bi ${isFullScreen ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'}`} aria-hidden="true" />
            <span className="visually-hidden">{isFullScreen ? __('Exit full screen') : __('View full screen')}</span>
          </button>
        </div>
        <Image 
          ref={imageRef}
          src={map.file_url} 
          alt={map.name || 'Building Map'} 
          className="w-100 h-100"
          style={{ 
            objectFit: 'contain',
            userSelect: 'none',
            pointerEvents: 'none'
          }}
          onLoad={() => {
            if (currentImageKey) {
              setLoadedImageKey(currentImageKey);
            }
            setImageLoaded(true);
          }}
          onError={() => {
            setImageLoaded(false);
            setLoadedImageKey(null);
          }}
        />
        
        {hasImageMetrics && validItems.map((item) => (
          <TrapMarker
            key={`${item.trap_id}-${resizeKey}-${dragUpdateKey}`}
            item={item}
            editMode={editMode}
            isSelected={selected && selected.some(s => s.trap_id === item.trap_id)}
            onMouseDown={(e) => handleMarkerMouseDown(e, item)}
            onClick={() => handleMarkerClick(item)}
            relativeToPixel={relativeToPixel}
            isDragging={dragging && dragging.trap_id === item.trap_id}
          />
        ))}

        {selected && selected.length > 0 && hasImageMetrics && (
          <TrapsInfo items={selected} onClose={handleCloseInfo} relativeToPixel={relativeToPixel} onRemoveFromMap={onRemoveFromMap} editMode={editMode} />
        )}
      </div>
    </div>
  );
};

const TrapMarker = memo(({ item, editMode, isSelected, onMouseDown, onClick, relativeToPixel, isDragging }) => {
  const getMarkerColor = (priority, eventName, isCaught) => {
    // Check for no_communication first (event_name takes precedence)
    if (eventName === 'no_communication') {
      return MarkerSymbols.no_comm.fillColor;
    }
    // Check for caught state - caught alarms/high are red
    if ([ 'alarm', 'high' ].includes(priority)) {
      return isCaught ? MarkerSymbols.alarm_catch.fillColor : MarkerSymbols.alarm.fillColor; // Orange for "not caught"
    }
    return MarkerSymbols.normal.fillColor;
  };

  // Always recalculate pixel position on render to handle resize and dragging
  const pixelPos = relativeToPixel(item.changed);

  const markerStyle = {
    position: 'absolute',
    left: `${pixelPos.x}px`,
    top: `${pixelPos.y}px`,
    transform: 'translate(-50%, -50%)',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: getMarkerColor(item.event_priority, item.event_name, item.is_caught),
    border: isSelected ? '3px solid #007bff' : '2px solid white',
    cursor: editMode ? 'move' : 'pointer',
    zIndex: isDragging ? 1001 : (isSelected ? 1000 : 100),
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
    transition: isDragging ? 'none' : 'all 0.2s ease',
    opacity: isDragging ? 0.8 : 1
  };

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onClick();
  }, [onClick]);

  return (
    <div
      style={markerStyle}
      onMouseDown={onMouseDown}
      onClick={handleClick}
      title={item.trap_name}
    />
  );
});

const TrapsInfo = ({ items, onClose, relativeToPixel, onRemoveFromMap, editMode }) => {
  const infoRef = useRef(null);
  const removeModal = useModalDialog();
  const [position, setPosition] = useState({ showBelow: false, left: 0, top: 0, arrowOffset: 0 });
  
  const firstItem = items[0];
  const pixelPos = relativeToPixel(firstItem.changed);
  
  useEffect(() => {
    if (!infoRef.current) return;
    
    const infoRect = infoRef.current.getBoundingClientRect();
    const containerRect = infoRef.current.offsetParent?.getBoundingClientRect() || { width: 0, height: 0, left: 0, top: 0 };
    
    const infoHeight = infoRect.height || 120; // Fallback height
    const infoWidth = infoRect.width || 200; // Fallback width
    const arrowHeight = 8;
    const margin = 10;
    
    // Check if there's enough space above the marker
    const spaceAbove = pixelPos.y;
    const spaceBelow = containerRect.height - pixelPos.y;
    const showBelow = spaceAbove < (infoHeight + arrowHeight + margin);
    
    // Calculate horizontal position to keep info window within bounds
    let left = pixelPos.x;
    const halfWidth = infoWidth / 2;
    
    if (left - halfWidth < margin) {
      left = halfWidth + margin; // Align to left edge with margin
    } else if (left + halfWidth > containerRect.width - margin) {
      left = containerRect.width - halfWidth - margin; // Align to right edge with margin
    }

    // Calculate arrow offset - how far the arrow should be from center
    const arrowOffset = pixelPos.x - left;

    // Calculate vertical position
    let top;
    if (showBelow) {
      top = pixelPos.y + 16 + arrowHeight; // Below marker + marker radius + arrow
    } else {
      top = pixelPos.y - infoHeight - arrowHeight - 16; // Above marker - info height - arrow - marker radius
    }
    
    // Ensure the info window doesn't go outside vertical bounds
    if (top < margin) {
      top = margin;
    } else if (top + infoHeight > containerRect.height - margin) {
      top = containerRect.height - infoHeight - margin;
    }
    
    setPosition({ showBelow, left, top, arrowOffset });
  }, [pixelPos.x, pixelPos.y]);
  
  const arrowStyle = {
    position: 'absolute',
    left: '50%',
    transform: `translateX(calc(-50% + ${position.arrowOffset}px))`,
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    ...(position.showBelow 
      ? {
          top: '-8px',
          borderBottom: '8px solid white',
          filter: 'drop-shadow(0 -2px 2px rgba(0,0,0,0.1))'
        }
      : {
          bottom: '-8px',
          borderTop: '8px solid white',
          filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))'
        }
    )
  };
  
  const handleRemove = useCallback(async (item, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!onRemoveFromMap) { return; }

    const trapLabel = item.trap_name || __('this trap');
    const html = __('Remove ${label} from the building map? No other data will be lost.').replace('${label}', `<b>${trapLabel}</b>`);

    const result = await removeModal.show({
      title: __('Remove from building map'),
      actionTitle: __('Remove'),
      variant: 'danger',
      html,
    });

    if (result) {
      await onRemoveFromMap(item);
      onClose();
    }
  }, [onRemoveFromMap, onClose, removeModal]);

  return (
    <div 
      ref={infoRef}
      className={cls(classnames.infoWindow)}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        left: `${position.left}px`,
        top: `${position.top}px`,
        transform: 'translateX(-50%)',
        zIndex: 1001,
        maxWidth: '250px',
        minWidth: '180px'
      }}
    >
      <div className="bg-white border rounded shadow p-2 position-relative">
        <div style={arrowStyle} />
        <div className="d-flex justify-content-between align-items-start mb-2">
          <span className="fw-bold"></span>
          <button 
            type="button" 
            className="btn-close btn-close-sm" 
            onClick={onClose}
            aria-label="Close"
          />
        </div>
        <table className="table table-sm mb-0">
          <tbody>
            {items.map((item) => {
              const { trap_id, trap_name, event_priority, is_caught, event_name } = item;

              return (
                <tr key={trap_id}>
                  <td className="align-middle" style={{ width: '16px', padding: '2px 4px', borderBottomWidth: 0 }}>
                    <EventPriority
                      priority={event_priority}
                      isCaught={is_caught}
                      eventName={event_name}
                    />
                  </td>
                  <td className="align-middle" style={{ padding: '2px 4px', borderBottomWidth: 0 }}>
                    <div className="d-flex align-items-center gap-col-2">
                      <NavLink to={generatePath('/trap/:trap_id', { trap_id })} className="text-decoration-none">
                        {trap_name}
                      </NavLink>
                      {editMode && (
                        <TooltipControl tooltip={__('Remove from map')} placement="top">
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0 d-inline-flex align-items-center justify-content-center lh-1"
                            onClick={(event) => handleRemove(item, event)}
                          >
                            <i className="bi bi-x-circle" aria-hidden="true" />
                            <span className="visually-hidden">{__('Remove from map')}</span>
                          </Button>
                        </TooltipControl>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ConfirmModalDialog {...removeModal.register()} />
    </div>
  );
};


